<template>
  <div class="promotion-deals-filter">
    <FiltersForm
      :has-filters="hasFilters"
      :is-skeleton-loading="isSkeletonLoading"
      :is-global-country-filter="false"
      @filter="onFilterClick"
      @clear="onClearFilters"
    >
      <platform-select
        v-model="selectedCountry"
        :is-multiple="false"
        :custom-countries="enabledPromoCountries"
        attach
      />

      <v-text-field
        clearable
        v-model="search"
        type="search"
        :label="$t('deal_id')"
        prepend-inner-icon="search"
      />

      <BaseSelect
        v-if="!soloPlacement"
        inner-icon="dns"
        label="Listings"
        :value="placementLabel"
        class="v-input"
      >
        <v-radio-group
          v-model="placementId"
          class="mt-0 pt-0"
          hide-details
        >
          <v-radio
            v-for="n in placementsTypes"
            :key="n.id"
            :label="n.name"
            :value="n.id"
            class="ma-2"
          ></v-radio>
        </v-radio-group>
      </BaseSelect>

      <MerchantFilter
        :country-code="selectedCountry"
        ref="customMerchantMenu"
        :set-merchants="setMerchants"
        label="Merchants"
        class="merchant-filter"
        hide-details
      />

      <v-autocomplete
        clearable
        v-model="group"
        :items="groups"
        prepend-inner-icon="category"
        item-title="name"
        item-value="id"
        :return-object="true"
        :label="$t('category')"
        :loading="isLoadingGroups"
        v-model:search="groupSearch"
      />
    </FiltersForm>
  </div>
</template>

<script>
  import { v1 as uuidv1 } from 'uuid';
  import stringEscape from 'js-string-escape';
  import FiltersForm from './FiltersBase';
  import { isEqual } from 'lodash-es'
  import MerchantFilter from './MerchantFilter';
  import {isStr, debounce, isNum, isNull} from '@/utils';
  import BaseSelect from '../../BaseSelect'

  const groupSearchDebouncer = debounce({
    id: uuidv1(),
    timeout: 500
  });

  export default {
    components: {
      MerchantFilter,
      FiltersForm,
      BaseSelect
    },
    props: {
      isSkeletonLoading: {
        type: Boolean,
        required: false
      },
      isDataLoaded: {
        type: Boolean,
        required: false
      },
      placementsTypes: {
        type: Array,
        required: true
      },
    },
    emits: ['clearFilters', 'filter', 'countryChanged'],
    data() {
      return {
        merchants: [],
        defaultMerchants: [],
        group: null,
        search: '',
        placementId: null,
        oldPlacementId: null,
        groupSearch: '',
        isLoadingGroups: false,
        isRefreshingGroups: false,
        selectedCountry: '',
        oldSelectedCountry: ''
      };
    },
    computed: {
      userCountries() {
        return this.$store.getters['users/countries'];
      },
      enabledPromoCountries() {
        return this.$store.getters['businessUsers/enabledPromoCountries'];
      },
      hasFilters() {
        const {
          search,
          isPlacementIdChanged,
          group,
          merchants,
          defaultMerchants,
          selectedCountry
        } = this;

        return (
          search !== '' ||
          isPlacementIdChanged ||
          isNum(group?.id) ||
          !isEqual(merchants, defaultMerchants) ||
          selectedCountry !== this.oldSelectedCountry
        );
      },
      isPlacementIdChanged() {
        if (this.oldPlacementId === null) return false;
        return this.placementId !== this.oldPlacementId;
      },
      placementLabel() {
        if (this.placementsTypes.length === 0 || this.placementId === null) return '';
        return this.placementsTypes.find(item => item.id === this.placementId).name
      },
      businessMerchants() {
        return this.$store.getters['promotions/businessMerchants'];
      },
      groups() {
        return this.$store.getters['pepper/groups'];
      },
      soloPlacement() {
        return this.placementsTypes.length === 1;
      },
    },
    watch: {
      selectedCountry(val) {
        // clear merchants to trigger autofill with business merchants
        if (this.$refs.customMerchantMenu) {
          this.$refs.customMerchantMenu.clearMerchants();
        }

        // prevent initial double call
        if (!this.isSkeletonLoading) {
          this.oldSelectedCountry = val;
          this.$emit('countryChanged', val);
        }
      },
      userCountries(val) {
        if (val.length > 0) {
          this.selectedCountry = val[0].code;
        }
      },
      isDataLoaded(val) {
        if (!val) return;

        this.defaultMerchants = [...this.merchants];

        if (this.placementId === null) {
          this.placementId = this.placementsTypes[0].id;
          this.oldPlacementId = this.placementsTypes[0].id;
        }

        this.onFilter();
      },
      businessMerchants(nextItems) {
        if (nextItems && isEqual(this.merchants, this.defaultMerchants)) {
          this.merchants = nextItems;
        }
        this.defaultMerchants = nextItems ? [...nextItems] : [];
      },
      placementsTypes(nextPlacements) {
        let placementId;

        if (nextPlacements.length === 1) {
          placementId = this.placementsTypes[0].id;
        } else {
          // eslint-disable-next-line array-callback-return
          this.placementsTypes.some(i => {
            if (i.id === 1) {
              placementId = i.id;
              return true;
            }
          });
        }

        this.placementId = placementId;
        this.oldPlacementId = placementId;
      },
      group() {
        this.isRefreshingGroups = true;
      },
      groupSearch(nextSearch) {
        if (isStr(nextSearch)) {
          if (this.isRefreshingGroups) {
            this.isRefreshingGroups = false;
            return false;
          }

          groupSearchDebouncer(() => this.getGroups(nextSearch));
        }
      },
    },
    created() {
      if (this.userCountries.length > 0) {
        this.selectedCountry = this.userCountries[0].code;
        this.oldSelectedCountry = this.userCountries[0].code;
      }

      if (isStr(this.$route.query.id)) {
        this.search = this.$route.query.id;
      }
    },
    methods: {
      getGroups(search = '') {
        const query = {
          search,
          country: this.selectedCountry
        };

        this.isLoadingGroups = true;

        return this.$store
          .dispatch('pepper/getGroups', query)
          .then((res) => {
            this.isLoadingGroups = false;
            return res;
          })
          .catch((err) => {
            this.isLoadingGroups = false;
            return err;
          });
      },
      addDealToPath(id) {
        this.$router.replace({ name: 'promotable-deals', query: { id } });
      },
      removeDealFromPath() {
        if (this.$route.query.id) {
          this.$router.push({ name: 'promotable-deals' });
        }
      },
      setMerchants(items) {
        this.merchants = items;
      },
      onFilterClick() {
        if (this.$refs.customMerchantMenu) {
          this.$refs.customMerchantMenu.showSaveText();
        }

        this.oldPlacementId = this.placementId;

        this.onFilter();
      },
      onFilter() {
        const { merchants, search, group, placementId, addDealToPath, selectedCountry } = this;

        if (search && !isStr(this.$route.query.id)) {
          addDealToPath(search);
          // adding the deal id to route path only if there is no other
          // id in the path already - for example after 'Reset filter'
        }

        this.$emit('filter', {
          merchant_ids: merchants.map((m) => m.id),
          group_id: group?.id || null,
          thread_id: isNull(search) ? '' : stringEscape(search),
          placement_id: placementId,
          country: selectedCountry
        });
      },
      onClearFilters() {
        this.group = null;
        this.search = '';
        this.merchants = this.businessMerchants.length ? this.businessMerchants : [];
        // eslint-disable-next-line array-callback-return
        this.placementsTypes.some(i => {
          if (i.id === 1) {
            this.placement_id = i.id;
            return true;
          }
        });

        if (this.$refs.customMerchantMenu) {
          this.$refs.customMerchantMenu.clearMerchants();
        }

        this.removeDealFromPath();
        this.$emit('clearFilters');
        this.onFilter();
      },
    },
  };
</script>

<style scoped lang="scss">
.merchant-filter {
  width: 100%;
  max-width: 270px;
}
</style>
